import { ChangeDetectionStrategy, Component, inject } from "@angular/core";
import { NewUiDashboardTalentPoolRequestData } from "@ankaadia/graphql";
import { EmptystateComponent } from "@ankaadia/ui";
import { derivedAsync } from "ngxtension/derived-async";
import { Tab, TabList, TabPanel, TabPanels, Tabs } from "primeng/tabs";
import { Skeleton } from "primeng/skeleton";
import { catchError, map, Observable, of } from "rxjs";
import { SettingsService } from "../../../shared/services/settings.service";
import { TranslateDirective } from "../../../shared/transloco/translate.directive";
import { NewUiDashboardCardHeaderComponent } from "../layout/new-ui-dashboard-card-header/new-ui-dashboard-card-header.component";
import { NewUiDashboardCardComponent } from "../layout/new-ui-dashboard-card/new-ui-dashboard-card.component";
import {
  DashboardListItem,
  NewUiDashboardListComponent,
} from "../layout/new-ui-dashboard-list/new-ui-dashboard-list.component";
import { NewUiDashboardService } from "../new-ui-dashboard.service";
import { TalentPoolConfigService } from "../../talent-pool-config/talent-pool-config.service";
import { TalentRequestStatusIconMap } from "../../talent-pool/talent-pool.constants";

@Component({
  selector: "new-ui-dashboard-talent-pool-requests",
  templateUrl: "./new-ui-dashboard-talent-pool-requests.component.html",
  imports: [
    TranslateDirective,
    NewUiDashboardCardComponent,
    NewUiDashboardCardHeaderComponent,
    NewUiDashboardListComponent,
    TabList,
    Tab,
    Tabs,
    TabPanels,
    TabPanel,
    EmptystateComponent,
    Skeleton,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewUiDashboardTalentPoolRequestsComponent {
  private readonly newUiDashboardService = inject(NewUiDashboardService);
  private readonly talentPoolConfigService = inject(TalentPoolConfigService);
  protected readonly settings = inject(SettingsService);

  protected readonly requests = derivedAsync(() => this.getTalentPoolRequests().pipe(catchError(() => of(null))));
  protected readonly config = derivedAsync(() =>
    this.talentPoolConfigService.getParticipationInfo().pipe(catchError(() => of(null)))
  );

  protected get showOutgoingTab(): boolean {
    return this.settings.hasModule("talentPool") || this.config()?.isUserInPool;
  }

  protected get showIncomingTab(): boolean {
    const cfg = this.config();
    return this.settings.hasModule("talentPool") || cfg?.isContributorInPool || cfg?.canContributeToAnkaadiaPool;
  }

  protected get defaultTab(): string {
    if (this.showOutgoingTab) return "0";
    if (this.showIncomingTab) return "1";
    return "0";
  }

  private getTalentPoolRequests(): Observable<{
    outgoing: DashboardListItem[];
    incoming: DashboardListItem[];
  } | null> {
    return this.newUiDashboardService.getTalentPoolRequests().pipe(
      map((data) => ({
        outgoing: data.outgoing.map((r) => this.mapRequestToListItem(r, "outgoing")),
        incoming: data.incoming.map((r) => this.mapRequestToListItem(r, "incoming")),
      })),
      catchError(() => of(null))
    );
  }

  private mapRequestToListItem(
    request: NewUiDashboardTalentPoolRequestData,
    direction: "outgoing" | "incoming"
  ): DashboardListItem {
    return {
      rowIcon: TalentRequestStatusIconMap[direction === "outgoing" ? "Approved" : "Pending"],
      rowIconStyleClass: direction === "outgoing" ? "text-success" : "text-info",
      name: request.candidateDisplayName,
      groupIcon: "icon-[mi--high-rise-building]",
      group: request.organizationName,
      routeLink: direction === "outgoing" ? "/app/talent-pools/view" : "/app/talent-pools/requests",
      queryParams: {
        tab: direction === "outgoing" ? "2" : "1",
      },
    };
  }
}
